import { type FunctionComponent, useMemo } from 'react';
import cn from 'classnames';
import toNumber from 'lodash/toNumber';
import { IconPhoto, type IconSize, type ImageProps, useStack } from '@direct-frontend/components';
import styles from './ImageBlank.module.css';

type ImageBlankProps = Pick<
    ImageProps,
    'data-testid' | 'className' | 'height' | 'width' | 'background' | 'borderRadius'
>;

export const ImageBlank: FunctionComponent<ImageBlankProps> = ({
    'data-testid': dataTestId = 'ImageBlank',
    className,
    width,
    height,
    background,
    borderRadius,
}) => {
    const cls = useStack({
        alignItems: 'center',
        justifyContent: 'center',
    });

    const style = useMemo(() => {
        const radius = borderRadius === 'round' ? '9999' : borderRadius;

        return {
            width: `${width}px`,
            height: `${height}px`,
            ...(radius ? { borderRadius: `${radius}px` } : {}),
        };
    }, [width, height, borderRadius]);

    const iconSize = useMemo<IconSize>(() => {
        const min = Math.min(toNumber(width), toNumber(height));

        if (min < 20) {
            return '8';
        } else if (min < 40) {
            return '16';
        } else if (min < 60) {
            return '24';
        }

        return '32';
    }, [height, width]);

    return (
        <div
            data-testid={dataTestId}
            className={cn(styles.container, className, cls, {
                [styles.bgWhite]: background === 'white',
                [styles.bgGray]: background === 'gray',
            })}
            style={style}
        >
            <IconPhoto role="supplementary" size={iconSize} color="gray" />
        </div>
    );
};
